import {
  type FocusEvent,
  type FormEvent,
  type ReactNode,
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg';
import clsx from 'clsx';
import Button from 'components/Button/Button';
import CategorySelector from 'components/CategorySelector/CategorySelector';
import Checkbox from 'components/Checkbox/Checkbox';
import IconButton from 'components/IconButton/IconButton';
import TableCell from 'components/Table/TableCell';
import TableRow from 'components/Table/TableRow';
import TextField from 'components/TextField/TextField';
import Tooltip from 'components/Tooltip/Tooltip';
import {
  ALPHANUMERIC_AND_NUMBER_REGEX,
  BOM_ADDITIONAL_INFO_MAX_LENGTH,
  BOM_DESCRIPTION_MAX_LENGTH,
  BOM_INPUT_PRODUCT_REGEX,
  BOM_SELECT_FIELDS,
  BOM_SOURCE_LABELS,
  BOM_SOURCE_VALUES,
  BOM_SUPPLIER_MAX_LENGTH,
  BOM_VISIBLE_COLUMNS,
  COMPANY_NAME_MIN_LENGTH,
  DATA_SETS_NAME_MAX_LENGTH,
  type TBomSelectField,
} from 'constants/general';
import { BUTTON_VARIANT, TOOLTIP_POSITIONS } from 'constants/interfaces';

import BomDescriptionModal from './BomDescriptionModal';

import type IOption from 'components/MappingModal/interfaces/IOption';
import type IUpdateAgentSessionBomItemBody from 'store/slices/valueChainMap/interfaces/IUpdateAgentSessionBomItemBody';
import type {
  TBomEditableCell,
  TBomRowDraft,
} from './interfaces/IRowAICreatorBoM';
import type IRowAICreatorBoM from './interfaces/IRowAICreatorBoM';

import styles from './sass/RowAICreatorBoM.module.scss';

type TBomValidatedField = 'name' | 'code';

function RowAICreatorBoM({
  item,
  rowIndex,
  naceOptions,
  categoryOptions,
  onUpdate,
  onDelete,
  focusCell,
  onStartEdit,
}: IRowAICreatorBoM) {
  const { t } = useTranslation();
  const rowRef = useRef<HTMLTableRowElement | null>(null);
  const suppliersCellRef = useRef<HTMLDivElement | null>(null);
  const nextEditingCellRef = useRef<TBomEditableCell | null>(null);
  const [editingCell, setEditingCell] = useState<TBomEditableCell | null>(null);
  const [draftItem, setDraftItem] = useState<TBomRowDraft>(item);
  const [supplierInput, setSupplierInput] = useState('');
  const [isDescriptionModalOpen, setIsDescriptionModalOpen] = useState(false);
  const [descriptionTooltipBelow, setDescriptionTooltipBelow] = useState(false);

  const normalizeNullableText = useCallback((value?: string) => {
    const normalizedValue = value?.trim();

    return normalizedValue || null;
  }, []);

  const formatSourceLabel = useCallback((value?: string | null) => {
    const normalizedValue = value?.trim();

    if (!normalizedValue) {
      return '';
    }

    if (normalizedValue.toLowerCase() === BOM_SOURCE_VALUES.AI) {
      return BOM_SOURCE_LABELS.AI;
    }

    if (normalizedValue.toLowerCase() === BOM_SOURCE_VALUES.VC) {
      return BOM_SOURCE_LABELS.VC;
    }

    if (normalizedValue.toLowerCase() === BOM_SOURCE_VALUES.VC_POTENTIAL) {
      return BOM_SOURCE_LABELS.VC_POTENTIAL;
    }

    return normalizedValue.charAt(0).toUpperCase() + normalizedValue.slice(1).toLowerCase();
  }, []);

  const normalizeSuppliers = useCallback((suppliers?: string[]) => suppliers
    ?.map((supplier) => supplier.trim())
    .filter(Boolean)
  || [], []);

  const parseProductCategoryId = useCallback((category?: string): number | null => {
    if (!category?.trim()) {
      return null;
    }

    const parsed = Number.parseInt(category, 10);

    return Number.isNaN(parsed) ? null : parsed;
  }, []);

  const buildRowBody = useCallback((draft: TBomRowDraft): IUpdateAgentSessionBomItemBody => {
    const normalizedSuppliers = normalizeSuppliers(draft.potentialSuppliers);
    const industrySectorValue = normalizeNullableText(draft.naceSector ?? draft.industrySector ?? undefined);

    const resolveProductCategoryId = (): number | null => {
      if (draft.category !== undefined) {
        return draft.category.trim() === '' ? null : parseProductCategoryId(draft.category);
      }

      return draft.productCategoryId ?? null;
    };

    return {
      isActive: draft.isActive,
      name: normalizeNullableText(draft.name),
      code: normalizeNullableText(draft.code),
      description: normalizeNullableText(draft.description),
      potentialSuppliers: normalizedSuppliers.length ? normalizedSuppliers : [],
      additionalInfo: normalizeNullableText(draft.additionalInfo),
      productCategoryId: resolveProductCategoryId(),
      industrySector: industrySectorValue,
    };
  }, [normalizeNullableText, normalizeSuppliers, parseProductCategoryId]);

  const areBodiesEqual = (
    currentBody: IUpdateAgentSessionBomItemBody,
    nextBody: IUpdateAgentSessionBomItemBody,
  ) => JSON.stringify(currentBody) === JSON.stringify(nextBody);

  useEffect(() => {
    setDraftItem(item);
  }, [item]);

  useEffect(() => {
    if (!editingCell) {
      return;
    }

    window.requestAnimationFrame(() => {
      const focusableElement = rowRef.current?.querySelector(
        [
          '[data-cell-editing="true"] input',
          '[data-cell-editing="true"] textarea',
          '[data-cell-editing="true"] [class*="control"]',
        ].join(', '),
      ) as HTMLElement | null;

      focusableElement?.focus();
    });
  }, [editingCell]);

  useEffect(() => {
    if (!focusCell || focusCell.itemId !== item.id) {
      return;
    }

    window.requestAnimationFrame(() => {
      rowRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
  }, [focusCell, item.id]);

  const itemBody = buildRowBody(item);

  const naceSectorKey = draftItem.naceSector ?? draftItem.industrySector ?? undefined;

  const naceValue = useMemo(
    () => naceOptions.find((option) => option.value === naceSectorKey) || null,
    [naceSectorKey, naceOptions],
  );

  const categoryKey = draftItem.category
    ?? (draftItem.productCategoryId != null ? String(draftItem.productCategoryId) : undefined);

  const categoryValue = useMemo(
    () => categoryOptions.find((option) => option.value === categoryKey) || null,
    [categoryOptions, categoryKey],
  );

  const getOptionLabel = useCallback((options: IOption[], value?: string) => (
    options.find((option) => option.value === value)?.label || value || ''
  ), []);

  const applyEditingState = useCallback((nextEditingCell: TBomEditableCell | null = null) => {
    setEditingCell(nextEditingCell);
    setSupplierInput('');
  }, []);

  const getValidatedFieldLabel = useCallback((field: TBomValidatedField) => {
    if (field === 'name') {
      return t('products.fields.productName');
    }

    return t('products.fields.productCode');
  }, [t]);

  const getFieldValidationError = useCallback((
    field: TBomValidatedField,
    value?: string | null,
  ) => {
    const normalizedValue = value?.trim() || '';
    const fieldLabel = getValidatedFieldLabel(field);

    if (!normalizedValue) {
      return '';
    }

    if (normalizedValue.length < COMPANY_NAME_MIN_LENGTH) {
      return t('errors.minCharactersLength', {
        name: fieldLabel,
        length: COMPANY_NAME_MIN_LENGTH,
      });
    }

    if (normalizedValue.length > DATA_SETS_NAME_MAX_LENGTH) {
      return t('errors.maxCharactersLength', {
        name: fieldLabel,
        length: DATA_SETS_NAME_MAX_LENGTH,
      });
    }

    if (
      field === 'name'
      && !BOM_INPUT_PRODUCT_REGEX.test(normalizedValue)
    ) {
      return t('errors.nameContainLettersAndNumbers');
    }

    if (
      field === 'code'
      && !ALPHANUMERIC_AND_NUMBER_REGEX.test(normalizedValue)
    ) {
      return t('errors.nameContainLettersAndNumbers');
    }

    return '';
  }, [getValidatedFieldLabel, t]);

  const getDraftValidationError = useCallback((
    draft: TBomRowDraft,
    fields: TBomValidatedField[],
  ) => {
    fields.forEach((field) => {
      const validationError = getFieldValidationError(field, draft[field]);

      if (validationError) {
        throw new Error(validationError);
      }
    });
  }, [getFieldValidationError]);

  const getNextDraftForCommit = useCallback(
    (): TBomRowDraft => draftItem,
    [draftItem],
  );

  const commitRow = useCallback(async (
    nextDraft: TBomRowDraft,
    nextEditingCell: TBomEditableCell | null = null,
  ) => {
    const currentEditingCell = editingCell;
    const fieldsToValidate = editingCell === 'name' || editingCell === 'code'
      ? [editingCell]
      : [];

    if (nextDraft.isActive && fieldsToValidate.length) {
      let validationError = '';

      try {
        getDraftValidationError(nextDraft, fieldsToValidate);
      } catch (error) {
        validationError = error instanceof Error ? error.message : '';
      }

      if (validationError) {
        toast.error(validationError);
        window.requestAnimationFrame(() => {
          const focusableElement = rowRef.current?.querySelector(
            [
              '[data-cell-editing="true"] input',
              '[data-cell-editing="true"] textarea',
            ].join(', '),
          ) as HTMLElement | null;

          focusableElement?.focus();
        });

        return false;
      }
    }

    const nextBody = buildRowBody(nextDraft);

    if (areBodiesEqual(itemBody, nextBody)) {
      applyEditingState(nextEditingCell);
      return true;
    }

    try {
      applyEditingState(nextEditingCell);
      await onUpdate(item.id, nextBody);
      return true;
    } catch {
      applyEditingState(currentEditingCell);
      return false;
    }
  }, [applyEditingState, buildRowBody, editingCell, getDraftValidationError, item.id, itemBody, onUpdate]);

  useEffect(() => {
    if (editingCell !== 'suppliers') {
      return undefined;
    }

    const handleSupplierOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node | null;

      if (!target || suppliersCellRef.current?.contains(target)) {
        return;
      }

      const nextEditingCell = nextEditingCellRef.current;
      nextEditingCellRef.current = null;
      commitRow(draftItem, nextEditingCell);
    };

    document.addEventListener('mousedown', handleSupplierOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleSupplierOutsideClick);
    };
  }, [commitRow, draftItem, editingCell]);

  useEffect(() => {
    if (editingCell !== 'suppliers') {
      return undefined;
    }

    const handleSupplierEsc = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }

      nextEditingCellRef.current = null;
      commitRow(draftItem, null);
    };

    document.addEventListener('keydown', handleSupplierEsc);

    return () => {
      document.removeEventListener('keydown', handleSupplierEsc);
    };
  }, [commitRow, draftItem, editingCell]);

  const handleStartEdit = useCallback((cell: TBomEditableCell) => {
    onStartEdit?.();
    setEditingCell(cell);
  }, [onStartEdit]);

  const handleDescriptionCellClick = useCallback(() => {
    onStartEdit?.();
    setIsDescriptionModalOpen(true);
  }, [onStartEdit]);

  const handleDescriptionMouseEnter = useCallback(() => {
    if (rowIndex < 3) {
      setDescriptionTooltipBelow(true);
      return;
    }

    if (!rowRef.current) {
      return;
    }

    const rowRect = rowRef.current.getBoundingClientRect();

    let scrollParent: HTMLElement | null = rowRef.current.parentElement;
    while (scrollParent) {
      const { overflowY } = window.getComputedStyle(scrollParent);
      if (overflowY !== 'visible') {
        break;
      }
      scrollParent = scrollParent.parentElement;
    }

    const containerTop = scrollParent?.getBoundingClientRect().top ?? 0;
    setDescriptionTooltipBelow(rowRect.top - containerTop < 80);
  }, [rowIndex]);

  const handleDescriptionModalClose = useCallback(() => {
    setIsDescriptionModalOpen(false);
  }, []);

  const handleDescriptionModalSave = async (newDescription: string) => {
    const nextDraft = {
      ...draftItem,
      description: newDescription.slice(0, BOM_DESCRIPTION_MAX_LENGTH),
    };
    setDraftItem(nextDraft);
    setIsDescriptionModalOpen(false);
    await commitRow(nextDraft);
  };

  const handleDraftChange = useCallback(<T extends keyof TBomRowDraft>(field: T, value: TBomRowDraft[T]) => {
    setDraftItem((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleSelectChange = useCallback((
    field: TBomSelectField,
    value: string,
  ) => {
    const nextDraft = {
      ...draftItem,
      [field]: value,
    };

    setDraftItem(nextDraft);
    commitRow(nextDraft);
  }, [commitRow, draftItem]);

  const handleSupplierAdd = useCallback(() => {
    const nextSupplier = supplierInput.trim();

    if (!nextSupplier) {
      return;
    }

    setDraftItem((prev) => ({
      ...prev,
      potentialSuppliers: [...normalizeSuppliers(prev.potentialSuppliers), nextSupplier],
    }));
    setSupplierInput('');
  }, [normalizeSuppliers, supplierInput]);

  const handleSupplierRemove = useCallback((supplierToRemove: string) => {
    setDraftItem((prev) => ({
      ...prev,
      potentialSuppliers: prev.potentialSuppliers.filter((supplier) => supplier !== supplierToRemove),
    }));
  }, []);

  const handleToggleActive = async () => {
    const previousDraft = draftItem;
    const nextIsActive = !draftItem.isActive;
    const nextDraft = {
      ...draftItem,
      isActive: nextIsActive,
    };

    setDraftItem(nextDraft);
    try {
      await onUpdate(item.id, { isActive: nextIsActive });
    } catch {
      setDraftItem(previousDraft);
    }
  };

  const handleDelete = () => {
    onDelete(item.id);
  };

  const handleCellBlur = (event: FocusEvent<HTMLDivElement>) => {
    const currentCell = event.currentTarget;
    if (!editingCell) {
      return;
    }

    const nextEditingCell = nextEditingCellRef.current;
    nextEditingCellRef.current = null;

    const relatedTarget = event.relatedTarget as Node | null;
    const focusMovedInside = relatedTarget ? currentCell.contains(relatedTarget) : false;

    if (!nextEditingCell && focusMovedInside) {
      return;
    }

    commitRow(getNextDraftForCommit(), nextEditingCell);
  };

  const handleTextSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    commitRow(draftItem);
  };

  const handleSupplierSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
  };

  const renderViewCell = (
    cell: TBomEditableCell,
    value: ReactNode,
    className?: string,
  ) => (
    <button
      type="button"
      className={styles.cellButton}
      onMouseDown={() => {
        nextEditingCellRef.current = editingCell && editingCell !== cell ? cell : null;
      }}
      onClick={(event) => {
        event.stopPropagation();

        if (editingCell && editingCell !== cell) {
          return;
        }

        handleStartEdit(cell);
      }}
    >
      <span className={className}>{value || <span className={styles.placeholder}>-</span>}</span>
    </button>
  );

  return (
    <>
      <TableRow ref={rowRef} className={styles.row}>
        <TableCell className={styles.checkboxCell}>
          <Checkbox
            name={`bom-item-active-${item.id}`}
            value={item.id}
            checked={draftItem.isActive}
            onChange={handleToggleActive}
            className={styles.checkbox}
          />
        </TableCell>
        <TableCell className={styles.contentCell}>
          <div
            className={clsx(
              styles.cellInner,
              {
                [styles.errorCell]:
                focusCell?.itemId === item.id && focusCell.field === 'name' && editingCell !== 'name',
              },
            )}
            data-cell-editing={editingCell === 'name'}
            onBlurCapture={handleCellBlur}
          >
            {editingCell === 'name' ? (
              <form className={styles.formField} onSubmit={handleTextSubmit}>
                <TextField
                  name={`bom-item-name-${item.id}`}
                  value={draftItem.name}
                  onChange={(event) => handleDraftChange('name', event.target.value)}
                  className={styles.textField}
                  maxLength={DATA_SETS_NAME_MAX_LENGTH}
                />
              </form>
            ) : renderViewCell('name', draftItem.name, styles.primaryValue)}
          </div>
        </TableCell>
        {BOM_VISIBLE_COLUMNS.CODE ? (
          <TableCell className={styles.codeCell}>
            <div
              className={clsx(
                styles.cellInner,
                {
                  [styles.errorCell]:
                focusCell?.itemId === item.id && focusCell.field === 'code' && editingCell !== 'code',
                },
              )}
              data-cell-editing={editingCell === 'code'}
              onBlurCapture={handleCellBlur}
            >
              {editingCell === 'code' ? (
                <form className={styles.formField} onSubmit={handleTextSubmit}>
                  <TextField
                    name={`bom-item-code-${item.id}`}
                    value={draftItem.code}
                    onChange={(event) => handleDraftChange('code', event.target.value)}
                    className={styles.textField}
                    maxLength={DATA_SETS_NAME_MAX_LENGTH}
                  />
                </form>
              ) : renderViewCell('code', draftItem.code, styles.cellText)}
            </div>
          </TableCell>
        ) : null}
        <TableCell className={styles.descriptionCell}>
          <div
            className={clsx(
              styles.cellInner,
              styles.descriptionCellInner,
              {
                [styles.errorCell]:
                focusCell?.itemId === item.id
                && focusCell.field === 'description'
                && editingCell !== 'description',
              },
            )}
            data-cell-editing={false}
          >
            <Tooltip
              hint={draftItem.description || undefined}
              className={styles.descriptionTooltipRoot}
              hintClassName={clsx(styles.descriptionHint, {
                [styles.descriptionHintBelow]: descriptionTooltipBelow,
                [styles.descriptionHintWide]: (draftItem.description?.length || 0) > 80,
              })}
              position={descriptionTooltipBelow ? TOOLTIP_POSITIONS.BOTTOM : undefined}
            >
              <button
                type="button"
                className={styles.cellButton}
                onMouseEnter={handleDescriptionMouseEnter}
                onMouseDown={() => {
                  if (editingCell && editingCell !== 'description') {
                    nextEditingCellRef.current = null;
                  }
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  handleDescriptionCellClick();
                }}
              >
                <span className={styles.cellText}>
                  {draftItem.description || <span className={styles.placeholder}>-</span>}
                </span>
              </button>
            </Tooltip>
          </div>
        </TableCell>
        {BOM_VISIBLE_COLUMNS.CATEGORY ? (
          <TableCell className={styles.categoryCell}>
            <div
              className={clsx(styles.cellInner, {
                [styles.errorCell]:
             focusCell?.itemId === item.id
                && focusCell.field === BOM_SELECT_FIELDS.CATEGORY
                && editingCell !== BOM_SELECT_FIELDS.CATEGORY,
              })}
              data-cell-editing={editingCell === BOM_SELECT_FIELDS.CATEGORY}
              onBlurCapture={handleCellBlur}
            >
              {editingCell === BOM_SELECT_FIELDS.CATEGORY ? (
                <CategorySelector
                  value={categoryValue}
                  options={categoryOptions}
                  onChange={(nextValue) => {
                    handleSelectChange(BOM_SELECT_FIELDS.CATEGORY, nextValue?.value || '');
                  }}
                  className={styles.selectField}
                  placeholder={t('common.selectCategory')}
                  searchPlaceholder={t('common.searchByName')}
                  openByDefault
                />
              ) : renderViewCell(
                BOM_SELECT_FIELDS.CATEGORY,
                getOptionLabel(categoryOptions, categoryKey),
                styles.cellText,
              )}
            </div>
          </TableCell>
        ) : null}
        <TableCell className={styles.suppliersCell}>
          <div
            ref={suppliersCellRef}
            className={clsx(
              styles.cellInner,
              {
                [styles.errorCell]:
                focusCell?.itemId === item.id
                && focusCell.field === 'suppliers'
                && editingCell !== 'suppliers',
              },
            )}
            data-cell-editing={editingCell === 'suppliers'}
            onBlurCapture={handleCellBlur}
          >
            {editingCell === 'suppliers' ? (
              <div className={styles.suppliersEditor}>
                <div className={styles.tags}>
                  {draftItem.potentialSuppliers.map((supplier) => (
                    <button
                      key={supplier}
                      type="button"
                      className={styles.tag}
                      onMouseDown={(event) => {
                        event.preventDefault();
                      }}
                      onClick={() => handleSupplierRemove(supplier)}
                      title={supplier}
                      aria-label={`${t('common.delete')}: ${supplier}`}
                    >
                      <span className={styles.tagLabel}>{supplier}</span>
                      <span className={styles.tagRemove} aria-hidden="true">x</span>
                    </button>
                  ))}
                </div>
                <form
                  className={styles.supplierInputRow}
                  onSubmit={handleSupplierSubmit}
                >
                  <TextField
                    name={`bom-item-suppliers-${item.id}`}
                    value={supplierInput}
                    onChange={(event) => setSupplierInput(
                      event.target.value.slice(0, BOM_SUPPLIER_MAX_LENGTH),
                    )}
                    className={styles.textField}
                  />
                  <Button
                    title={t('common.add')}
                    variant={BUTTON_VARIANT.primary}
                    className={styles.addSupplierButton}
                    onMouseDown={(event) => {
                      event.preventDefault();
                    }}
                    onClick={handleSupplierAdd}
                    disabled={!supplierInput.trim()}
                  />
                </form>
                <span className={styles.helperText}>Click Add to add keyword</span>
              </div>
            ) : renderViewCell(
              'suppliers',
              draftItem.potentialSuppliers.length ? (
                <span className={styles.tags}>
                  {draftItem.potentialSuppliers.map((supplier) => (
                    <span key={supplier} className={styles.tagStatic} title={supplier}>
                      <span className={styles.tagLabel}>{supplier}</span>
                    </span>
                  ))}
                </span>
              ) : null,
              styles.suppliersValue,
            )}
          </div>
        </TableCell>
        {BOM_VISIBLE_COLUMNS.NACE ? (
          <TableCell className={styles.naceCell}>
            <div
              className={clsx(
                styles.cellInner,
                {
                  [styles.errorCell]:
                focusCell?.itemId === item.id
                && focusCell.field === BOM_SELECT_FIELDS.NACE_SECTOR
                && editingCell !== BOM_SELECT_FIELDS.NACE_SECTOR,
                },
              )}
              data-cell-editing={editingCell === BOM_SELECT_FIELDS.NACE_SECTOR}
              onBlurCapture={handleCellBlur}
            >
              {editingCell === BOM_SELECT_FIELDS.NACE_SECTOR ? (
                <CategorySelector
                  value={naceValue}
                  options={naceOptions}
                  onChange={(nextValue) => {
                    handleSelectChange(BOM_SELECT_FIELDS.NACE_SECTOR, nextValue?.value || '');
                  }}
                  className={styles.selectField}
                  placeholder={t('common.select')}
                  searchPlaceholder={t('common.searchByName')}
                  openByDefault
                />
              ) : renderViewCell(
                BOM_SELECT_FIELDS.NACE_SECTOR,
                getOptionLabel(naceOptions, naceSectorKey),
                styles.cellText,
              )}
            </div>
          </TableCell>
        ) : null}
        <TableCell className={styles.additionalInfoCell}>
          <div
            className={clsx(
              styles.cellInner,
              {
                [styles.errorCell]:
                focusCell?.itemId === item.id
                && focusCell.field === 'additionalInfo'
                && editingCell !== 'additionalInfo',
              },
            )}
            data-cell-editing={editingCell === 'additionalInfo'}
            onBlurCapture={handleCellBlur}
          >
            {editingCell === 'additionalInfo' ? (
              <form className={styles.formField} onSubmit={handleTextSubmit}>
                <TextField
                  name={`bom-item-additional-${item.id}`}
                  value={draftItem.additionalInfo}
                  onChange={(event) => handleDraftChange('additionalInfo', event.target.value)}
                  className={styles.textField}
                  maxLength={BOM_ADDITIONAL_INFO_MAX_LENGTH}
                />
              </form>
            ) : renderViewCell('additionalInfo', draftItem.additionalInfo, styles.cellText)}
          </div>
        </TableCell>
        <TableCell className={styles.sourceCell}>
          <div
            className={clsx(
              styles.cellInner,
              {
                [styles.errorCell]:
                focusCell?.itemId === item.id && focusCell.field === 'source' && editingCell !== 'source',
              },
            )}
            data-cell-editing={false}
            onBlurCapture={handleCellBlur}
          >
            <div className={styles.sourceValueWrap}>
              {draftItem.source ? (
                <span className={styles.sourceBadge}>{formatSourceLabel(draftItem.source)}</span>
              ) : <span className={styles.placeholder}>-</span>}
            </div>
          </div>
        </TableCell>
        <TableCell className={styles.actionsCell}>
          <IconButton
            icon={<DeleteIcon />}
            onClick={handleDelete}
            className={styles.deleteButton}
          />
        </TableCell>
      </TableRow>
      <BomDescriptionModal
        open={isDescriptionModalOpen}
        value={draftItem.description || ''}
        onSave={handleDescriptionModalSave}
        onClose={handleDescriptionModalClose}
      />
    </>
  );
}

export default RowAICreatorBoM;
